import type {DynamoEnvValueType} from '../types';
import {TypeImplementation, withSystem, EnvTypes} from '@packages/systems/wfdl';
import {functionOf, TText} from '@packages/systems/wfdl-ast';
import {qualify, iconName} from '@packages/systems/wfdl-Qualified';
import {pluginName} from '../constants';
import {TOption} from '@packages/systems/plugins/Option';
import {TBasicLink} from '../Link';
import {TDynamoFullSlug} from '@packages/systems/plugins-utils/DynamoGateway';
import {dynamoTextualContentToText} from '../transformers';
import {
  LinkPreloadValue,
  LinkTargetValue,
  TLinkPreload,
  TLinkTarget,
} from '../toBasicLinkV2Shared';

export const FullSlugType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(qualify(pluginName, iconName('Link'))),
});

// FullSlug is a CMS-computed URL path (e.g. `/cms-items/<slug>`). Hidden by
// default and opt-in via enableCmsImplicitsForEmbed so the URL Path option
// only appears in the embed editor's binding picker -- matching the surface
// the classic embed editor exposed without leaking it into every Text-typed
// binding picker product-wide.
export const dynamoFullSlugToText = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoFullSlug)),
      TText
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: dynamoTextualContentToText,
} as const;

type DynpageLinkLiteral = {
  mode: 'dynpage';
  pageSlug: string;
  target?: '_blank';
  preload?: 'prefetch' | 'prerender';
};

/**
 * @deprecated - replaced by the V2 implicit below
 *
 * Should be exported as hidden to ensure backwards compatibility before
 * we have run the site data migration to remove calls to this function
 * from element data.
 *
 * Should be deleted after the migration has been shipped.
 */
export const dynamoFullSlugToBasicLink = {
  envType: {
    dataType: functionOf(
      withSystem({isBindable: true}, TOption(TDynamoFullSlug)),
      TBasicLink
    ),
    exported: 'hidden',
    implicit: 'prefilledArgs',
  },
  value: (option: DynamoEnvValueType<string>): DynpageLinkLiteral => {
    if (option === null || option.tag === 'None') {
      return {
        pageSlug: '',
        mode: 'dynpage',
      };
    }

    return {
      pageSlug: option.val,
      mode: 'dynpage',
    };
  },
} as const;

export const dynamoFullSlugToBasicLinkV2: EnvTypes.TypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'Link settings'},
      functionOf(
        TLinkTarget,
        functionOf(
          TLinkPreload,
          functionOf(
            withSystem({isBindable: true}, TOption(TDynamoFullSlug)),
            TBasicLink
          )
        )
      )
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  value: (target: LinkTargetValue) => {
    return (preload: LinkPreloadValue) => {
      return (option: DynamoEnvValueType<string>): DynpageLinkLiteral => {
        const openInNewTabBool = target.tag === 'New tab';
        const preloadValue = preload.tag.toLowerCase() as
          | 'none'
          | 'prefetch'
          | 'prerender';
        if (option === null || option.tag === 'None' || !option.val) {
          return {
            pageSlug: '',
            mode: 'dynpage',
          };
        }

        return {
          pageSlug: option.val,
          mode: 'dynpage',
          ...(preloadValue !== 'none' ? {preload: preloadValue} : null),
          ...(openInNewTabBool ? {target: '_blank'} : {}),
        };
      };
    };
  },
};
